'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Tip {
  id: string;
  season: string;
  icon: string;
  title: string;
  tips: string[];
  color: string;
}

interface SeasonalTipsProps {
  className?: string;
}

const SeasonalTips = ({ className = '' }: SeasonalTipsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeSeason, setActiveSeason] = useState<string>('rainy');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const seasonalTips: Tip[] = [
    {
      id: 'rainy',
      season: 'Musim Hujan',
      icon: 'CloudIcon',
      color: 'from-blue-500 to-blue-600',
      title: 'Tips Musim Hujan',
      tips: [
        'Keringkan pakaian dalam rumah dengan kipas atau dehumidifier',
        'Elakkan jemur pakaian di luar untuk hindari bau hapak',
        'Guna pewangi kain untuk kekalkan kesegaran',
        'Pastikan pakaian benar-benar kering sebelum disimpan',
        'Simpan pakaian dalam almari dengan silica gel',
      ],
    },
    {
      id: 'hot',
      season: 'Musim Panas',
      icon: 'SunIcon',
      color: 'from-orange-500 to-red-500',
      title: 'Tips Musim Panas',
      tips: [
        'Jemur pakaian di bawah sinaran matahari untuk bunuh bakteria',
        'Basuh pakaian lebih kerap kerana berpeluh',
        'Guna detergen dengan pewangi segar',
        'Elakkan jemur terlalu lama untuk hindari warna pudar',
        'Simpan pakaian musim sejuk dengan moth balls',
      ],
    },
    {
      id: 'festive',
      season: 'Musim Perayaan',
      icon: 'SparklesIcon',
      color: 'from-purple-500 to-pink-500',
      title: 'Tips Musim Perayaan',
      tips: [
        'Hantar pakaian raya/perayaan awal untuk elak kesesakan',
        'Guna servis premium untuk pakaian istimewa',
        'Pastikan pakaian diseterika dengan kemas',
        'Simpan pakaian dalam cover untuk kekalkan kesegaran',
        'Periksa butang dan jahitan sebelum hantar',
      ],
    },
    {
      id: 'school',
      season: 'Musim Persekolahan',
      icon: 'AcademicCapIcon',
      color: 'from-green-500 to-teal-500',
      title: 'Tips Musim Persekolahan',
      tips: [
        'Basuh pakaian sekolah setiap hari untuk kekalkan kebersihan',
        'Guna pemutih untuk pakaian putih (jika perlu)',
        'Seterika pakaian untuk penampilan kemas',
        'Periksa kesan dakwat dan buang segera',
        'Sediakan pakaian ganti untuk kecemasan',
      ],
    },
  ];

  if (!isHydrated) {
    return (
      <section className={`py-16 bg-muted ${className}`}>
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-10 bg-card rounded w-1/2 mx-auto mb-12"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-20 bg-card rounded-lg"></div>
              ))}
            </div>
            <div className="h-64 bg-card rounded-xl"></div>
          </div>
        </div>
      </section>
    );
  }

  const currentTip = seasonalTips.find((t) => t.id === activeSeason) || seasonalTips[0];

  return (
    <section className={`py-16 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-textPrimary mb-4">
            Tips Penjagaan Mengikut Musim
          </h2>
          <p className="text-lg text-textSecondary font-body max-w-2xl mx-auto">
            Setiap musim ada cara penjagaan yang berbeza. Ikuti tips kami untuk hasil terbaik.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 max-w-4xl mx-auto">
          {seasonalTips.map((tip) => (
            <button
              key={tip.id}
              onClick={() => setActiveSeason(tip.id)}
              className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                activeSeason === tip.id
                  ? 'border-primary bg-primary/10 shadow-md'
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <Icon
                name={tip.icon as any}
                size={32}
                variant={activeSeason === tip.id ? 'solid' : 'outline'}
                className={
                  activeSeason === tip.id
                    ? 'text-primary mx-auto mb-2'
                    : 'text-textSecondary mx-auto mb-2'
                }
              />
              <div className="text-sm font-body font-semibold text-textPrimary text-center">
                {tip.season}
              </div>
            </button>
          ))}
        </div>

        <div className="max-w-4xl mx-auto">
          <div
            className={`bg-gradient-to-r ${currentTip.color} rounded-xl shadow-lg p-8 text-white`}
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                <Icon
                  name={currentTip.icon as any}
                  size={32}
                  variant="outline"
                  className="text-white"
                />
              </div>
              <h3 className="text-2xl font-heading font-bold">{currentTip.title}</h3>
            </div>

            <div className="space-y-3">
              {currentTip.tips.map((tip, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4"
                >
                  <div className="bg-white/20 rounded-full p-1 flex-shrink-0 mt-0.5">
                    <Icon name="CheckIcon" size={16} variant="outline" className="text-white" />
                  </div>
                  <p className="text-white/95 font-body leading-relaxed">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeasonalTips;
